import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent,
  DialogTitle, Snackbar, Switch, TextField, Typography,
} from '@mui/material';
import { Download, Fingerprint, Home, Logout, Sync, Upload } from '@mui/icons-material';
import { APP_VERSION } from '../version';
import { useLanguage } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import { useBiometric } from '../hooks/useBiometric';
import { LanguageSelector } from './LanguageSelector';
import { Gateway } from '../types';
import { MAX_WIDTH } from '../App';
import { gatewaysKey, makeHouseId } from '../context/houseStorage';

interface Props {
  gateways: Gateway[];
  onImport: (gateways: Gateway[]) => void;
  onSyncFromPi: () => void;
}

interface ConfFile {
  clientId: string;
  domain: string;
  username: string;
  gateways: Gateway[];
}

export const SettingsPage: React.FC<Props> = ({ gateways, onImport, onSyncFromPi }) => {
  const { t } = useLanguage();
  const { clientId, domain, username, restore, logout, goToHousePicker, houses, activeHouseId } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const biometric = useBiometric(activeHouseId ?? '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [biometricDialogOpen, setBiometricDialogOpen] = useState(false);
  const [biometricPassword, setBiometricPassword] = useState('');
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const handleExport = () => {
    const data: ConfFile = { clientId, domain, username, gateways };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${username}.conf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data: ConfFile = JSON.parse(ev.target?.result as string);
        if (!data.clientId || !data.domain || !data.username || !Array.isArray(data.gateways)) {
          throw new Error('invalid');
        }
        onImport(data.gateways);
        restore(data.clientId, data.domain, data.username);
        // restore() may rename the active house's id (and, via renameHouseData,
        // move whatever was previously stored under the old id's gateways key
        // onto the new one). Persist the imported gateways directly, keyed by
        // the resulting id, rather than relying on AuthenticatedApp's persist
        // effect: restore() flips isAuthenticated to false in the same batch
        // as onImport(), which unmounts AuthenticatedApp (App.tsx keys it on
        // activeHouseId) before that effect ever gets a chance to run with the
        // new value -- silently discarding the import otherwise.
        const newHouseId = makeHouseId(data.clientId, data.domain);
        const clean = data.gateways.map(({ lastFetchedAt: _lfa, ...g }) => ({
          ...g,
          nodes: g.nodes.map(({ isLoading: _, error: __, ...n }) => n),
        }));
        localStorage.setItem(gatewaysKey(newHouseId), JSON.stringify(clean));
        setSnackbar({ message: t.settingsImportSuccess, severity: 'success' });
      } catch {
        setSnackbar({ message: t.settingsImportError, severity: 'error' });
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleEnableBiometric = async () => {
    if (!biometricPassword.trim()) return;
    const ok = await biometric.enroll(biometricPassword);
    setBiometricDialogOpen(false);
    setBiometricPassword('');
    setSnackbar(ok
      ? { message: t.biometricEnable + ' ✓', severity: 'success' }
      : { message: t.biometricError, severity: 'error' }
    );
  };

  return (
    <Box sx={{ maxWidth: MAX_WIDTH, mx: 'auto', width: '100%', p: 2 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>{t.settingsTitle}</Typography>

      {/* Export */}
      <Box sx={{ mb: 3 }}>
        <Button variant="contained" startIcon={<Download />} onClick={handleExport} fullWidth sx={{ mb: 1 }}>
          {t.settingsExport}
        </Button>
        <Typography variant="caption" color="text.secondary">{t.settingsExportDesc}</Typography>
      </Box>

      {/* Import */}
      <Box sx={{ mb: 3 }}>
        <input
          ref={fileInputRef} type="file" accept=".conf,application/json"
          style={{ display: 'none' }} onChange={handleFileChange}
        />
        <Button variant="outlined" startIcon={<Upload />} onClick={() => fileInputRef.current?.click()} fullWidth sx={{ mb: 1 }}>
          {t.settingsImport}
        </Button>
        <Typography variant="caption" color="text.secondary">{t.settingsImportDesc}</Typography>
      </Box>

      {/* Biometric */}
      {biometric.isAvailable && (
        <Box sx={{ mb: 3 }}>
          {biometric.isEnrolled ? (
            <Button variant="outlined" color="error" startIcon={<Fingerprint />} onClick={biometric.unenroll} fullWidth sx={{ mb: 1 }}>
              {t.biometricDisable}
            </Button>
          ) : (
            <Button variant="outlined" startIcon={<Fingerprint />} onClick={() => setBiometricDialogOpen(true)} fullWidth sx={{ mb: 1 }}>
              {t.biometricEnable}
            </Button>
          )}
          <Typography variant="caption" color="text.secondary">
            {biometric.isEnrolled ? '' : t.biometricEnableDesc}
          </Typography>
        </Box>
      )}

      {/* Theme */}
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ flex: 1 }}>{t.settingsTheme}</Typography>
        <Typography variant="body2" color="text.secondary">{t.settingsThemeLight}</Typography>
        <Switch checked={mode === 'dark'} onChange={toggleMode} color="primary" />
        <Typography variant="body2" color="text.secondary">{t.settingsThemeDark}</Typography>
      </Box>

      {/* Language */}
      <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>Language / Lingua</Typography>
        <LanguageSelector />
      </Box>

      {/* Sync from Pi */}
      <Box sx={{ mb: 3 }}>
        <Button variant="outlined" startIcon={<Sync />} onClick={onSyncFromPi} fullWidth sx={{ mb: 1 }}>
          {t.settingsSyncFromPi}
        </Button>
        <Typography variant="caption" color="text.secondary">{t.settingsSyncFromPiDesc}</Typography>
      </Box>

      {/* Switch / add house */}
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<Home />} onClick={goToHousePicker} fullWidth>
          {houses.length > 1 ? t.settingsSwitchHouse : t.settingsAddHouse}
        </Button>
      </Box>

      {/* Logout */}
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" color="error" startIcon={<Logout />} onClick={logout} fullWidth>
          {t.settingsLogout}
        </Button>
      </Box>

      {/* Reset */}
      <Box>
        <Button variant="outlined" color="error" onClick={() => setResetDialogOpen(true)} fullWidth sx={{ mb: 1 }}>
          {t.settingsReset}
        </Button>
        <Typography variant="caption" color="text.secondary">{t.settingsResetDesc}</Typography>
      </Box>

      {/* Reset confirmation dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t.settingsReset}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{t.settingsResetConfirm}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>{t.nodeEditCancel}</Button>
          <Button color="error" variant="contained" onClick={() => { localStorage.clear(); window.location.reload(); }}>
            {t.settingsResetButton}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Biometric enable dialog */}
      <Dialog open={biometricDialogOpen} onClose={() => setBiometricDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{t.biometricEnable}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t.biometricEnableDesc}</Typography>
          <TextField
            fullWidth autoFocus type="password" label={t.loginPassword}
            value={biometricPassword}
            onChange={e => setBiometricPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleEnableBiometric()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBiometricDialogOpen(false); setBiometricPassword(''); }}>{t.nodeEditCancel}</Button>
          <Button variant="contained" onClick={handleEnableBiometric}>{t.biometricEnable}</Button>
        </DialogActions>
      </Dialog>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 3 }}>
        v{APP_VERSION}
      </Typography>

      <Snackbar
        open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar?.severity} onClose={() => setSnackbar(null)} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
